import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';


// Icons
import MenuRounded from '@mui/icons-material/MenuRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import InfoRounded from '@mui/icons-material/InfoRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import ContactMailRounded from '@mui/icons-material/ContactMailRounded';

// Logo asset.  Adjust the import path to match the location of
// `logo.png` in your project.  For example, if your logos live in
// `src/assets` then the path would be '../assets/logo.png'.
import logo from '../assets/logo.png';

/**
 * Customised header component that displays a logo on the left and a
 * navigation menu on the right.  The menu uses a drawer on mobile
 * viewports and inline links on desktop.  The bar has a glass style
 * translucency which adjusts opacity when the user scrolls.
 */
const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // Trigger when scrolled 80px down to change the header background
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 80 });

  // Define the navigation items with labels, href targets and icons
  const navItems = [
    { label: 'Inicio', href: '#inicio', icon: HomeRounded },
    { label: 'Servicios', href: '#servicios', icon: LocalShippingRounded },
    { label: 'Sobre Nosotros', href: '#sobre-nosotros', icon: InfoRounded },
    { label: 'Aliados', href: '#aliados', icon: HandshakeRounded },
    { label: 'Contacto', href: '#contacto', icon: ContactMailRounded },
  ];

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };
 const pearl = '#FFFFFF';
 const pearlGlass = alpha(pearl, 0.72);

  return (
    <React.Fragment>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          py: 0.5,
          px: { xs: 1.5, md: 3 },
          transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease',
                 bgcolor: pearlGlass,
          boxShadow: '0 4px 16px rgba(0,0,0,.04)',
          backdropFilter: 'blur(14px)',
          color: 'text.primary',
        }}
      >
        <Toolbar disableGutters>
          {/* Logo on the left */}
          <Box
            component="a"
            href="#inicio"
            sx={{ display: 'flex', alignItems: 'center', mr: 2, textDecoration: 'none' }}
          >
              <Box component="img" src={logo} alt="Acopiaserwa logo" sx={{ height: 44 }} />
          </Box>
          {/* Expand space between logo and menu */}
          <Box sx={{ flexGrow: 1 }} />
          {/* Desktop navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {navItems.map((item) => (
                <Box
                  key={item.href}
                  component="a"
                  href={item.href}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                   color: 'text.primary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    transition: 'color 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                      color: theme.palette.secondary.main,
                    },
                  }}
                >
                  {React.createElement(item.icon, { fontSize: 'small' })}
                  <span>{item.label}</span>
                </Box>
              ))}
            </Box>
          )}
          {/* Mobile menu icon */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
            >
              <MenuRounded />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      {/* Mobile navigation drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
           bgcolor: pearlGlass,
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 16px rgba(0,0,0,.04)',
            color: 'text.primary',
          },
        }}
      >
        <Box
          role="presentation"
          sx={{ width: 240, mt: 2 }}
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {navItems.map((item) => (
              <ListItem button component="a" href={item.href} key={item.href}>
                <ListItemIcon>
                  {React.createElement(item.icon, { color: 'inherit' })}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </React.Fragment>
  );
};

export default Header;